import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';

import type { IDataColumn } from '@/design';
import type { IListField, IListFieldCatalogue } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { IWorkItemRow } from '../../api/work-item-contracts';
import { refusalMessage } from '../../refusal';
import { cellText, LONG_TEXT_WHOLE } from '../lists/cells';

/**
 * The survey questions a work item can be read with (SR-398): a survey queue's *Columns* offers its
 * survey's questions as Lists' catalogue names them — the "Survey questions" group, `q.` and
 * `rep.` codes, with the field picker's words and provenance — and each chosen one is a column of
 * the item's participant's answer on that survey, as their Lists row holds it (their newest live
 * response there).
 *
 * **A team member's or a principal's question is one column, the flagged member's** — `rep.flagged.`
 * in place of Lists' five `rep.<slot>.` columns: survey-api reads it in the member row whose CRD is
 * the item's, so each row shows the answer of the person it is about. Other repeat groups (revenue
 * shares, community organisations) keep their slots: they are not about a person.
 *
 * **All offers one column over every queue, the U4 explanation** (`u4.explanation`): survey-api reads
 * each row's in its own survey — FA's and FSP's question, the flagged member's on TEAMS and RIA_FIRM.
 * A survey tab offers its own questions instead.
 *
 * **An answer of a survey not yet submitted says so** — a "draft" mark before it: the queue reads
 * the newest response, as Lists does, and a draft's answer can still change.
 *
 * **Only the chosen questions are asked for** (`fields=`), unlike the staff fields: a survey holds
 * hundreds of questions and long texts, so the rows read follows the choice.
 *
 * **An empty answer is a blank cell**, never "—" or an error (AC2): most questions are unanswered
 * by most people. A long text — an explanation of a disclosure — draws its first three lines,
 * whole on hover; a question's header is cut to one line, whole on hover.
 */

/** survey-api's U4 explanation over every queue (`WorkItemAnswerCodes.U4Explanation`). */
export const U4_EXPLANATION = 'u4.explanation';

/** survey-api's prefixes of an answer's field (`ListFieldRules.AnswerPrefix`, `RepPrefix`). */
const ANSWER_PREFIX = 'q.';
const REPEAT_SLOT_PREFIX = 'rep.';

/** Whether a code is a survey question's — what `fields=` reads into `answers`. */
export function isAnswerCode(code: string): boolean {
  return code === U4_EXPLANATION || code.startsWith(ANSWER_PREFIX) || code.startsWith(REPEAT_SLOT_PREFIX);
}

/** The queues whose survey asks for a U4 explanation (`WorkItemAnswerCodes.U4ExplanationSources`). */
export const U4_SURVEYS: ReadonlySet<string> = new Set(['FA', 'FSP', 'TEAMS', 'RIA_FIRM']);

const U4_FIELD: IListField = {
  code: U4_EXPLANATION,
  label: 'U4 explanation',
  number: null,
  kind: 'long_text',
  options: null,
  column: null,
  hot: false,
  editable: false,
  editType: null,
  filterable: false,
  sortable: false,
  groupable: false,
  summable: false,
  access: 'staff',
  anomalyThreshold: null,
  provenance: { origin: 'answer', cycle: 'latest_response', prefill: [], drawsOn: [] }
};

/** survey-api's flagged member's question (`WorkItemAnswerCodes.FlaggedPrefix`). */
const FLAGGED_PREFIX = 'rep.flagged.';

/** The questions of the member rows survey-api matches an item's CRD in — a mirror of the questions
 *  of `RosterShapes`' groups, Teams' members and an RIA firm's principals; a question it does not
 *  hold is refused there, so the two move together. */
const MEMBER_QUESTIONS = ['teams.member.', 'ria.principal.'];

const SLOT = /^rep\.(\d+)\.(.+)$/;

/** The answer fields with a member question's slots folded into one flagged member's field, at its
 *  first slot's place — "Team members 2: We have…" becomes "Flagged member: We have…". */
function withFlaggedMembers(fields: readonly IListField[]): IListField[] {
  const folded: IListField[] = [];
  const seen = new Set<string>();
  for (const field of fields) {
    const [, slot, question] = SLOT.exec(field.code) ?? [];
    if (!question || !MEMBER_QUESTIONS.some((prefix) => question.startsWith(prefix))) {
      folded.push(field);
    } else if (!seen.has(question)) {
      seen.add(question);
      // survey-api's label is "<group> <slot>: <question>".
      const asked = field.label.replace(new RegExp(`^.*?\\s${slot}:\\s*`), '');
      folded.push({ ...field, code: `${FLAGGED_PREFIX}${question}`, label: `Flagged member: ${asked}` });
    }
  }
  return folded;
}

/** A queue's survey questions, or what stopped them being read — the queue still draws without them. */
export interface IAnswerColumns {
  catalogue: Nullable<IListFieldCatalogue>;
  fields: Map<string, IListField>;
  error: Nullable<string>;
}

const NO_ANSWERS: IAnswerColumns = { catalogue: null, fields: new Map(), error: null };

/** What All offers: the U4 explanation alone, every queue's rows carrying it. */
export const ALL_QUEUES_ANSWERS: IAnswerColumns = {
  catalogue: { survey: 'all', groups: [{ source: 'answer', fields: [U4_FIELD] }] },
  fields: new Map([[U4_EXPLANATION, U4_FIELD]]),
  error: null
};

/** The survey's Lists catalogue cut to its survey questions — the answer fields held in the row's
 *  documents, which the queue's rows can carry. */
export async function readAnswerColumns(surveyCode: string, signal?: AbortSignal): Promise<IAnswerColumns> {
  try {
    const catalogue = await listsApi.fields(surveyCode, signal);
    if (!catalogue) return NO_ANSWERS;
    const groups = catalogue.groups
      .filter((group) => group.source === 'answer')
      .map((group) => ({
        ...group,
        fields: withFlaggedMembers(group.fields.filter((field) => field.column === null))
      }));
    // Every field, a hidden one too: the sheet leaves out one nobody chose, and keeps a chosen one
    // that was hidden since, so it can still be named and removed (SR-462, as Lists does).
    const fields = new Map(groups.flatMap((group) => group.fields.map((field) => [field.code, field] as const)));
    return { catalogue: { ...catalogue, groups }, fields, error: null };
  } catch (failure) {
    if (signal?.aborted) throw failure;
    return { ...NO_ANSWERS, error: refusalMessage(failure, 'Could not read the survey questions') };
  }
}

/** One answer as a row carries it, in Lists' words — blank where the participant has none; `draft`
 *  marks one of a survey not yet submitted. */
function AnswerValue({ field, value, draft }: { field: IListField; value: unknown; draft: boolean }) {
  const text = value === undefined ? '' : cellText(field, { v: value });
  const long = field.kind === 'long_text';
  return (
    <span
      data-cell={field.code}
      data-empty={text === '' ? '' : undefined}
      title={long && text !== '' ? text : undefined}
      className={cn('block', long ? cn(LONG_TEXT_WHOLE, 'line-clamp-3 w-[20rem]') : 'max-w-[16rem] truncate')}
    >
      {draft && text !== '' && <DraftMark />}
      {text}
    </span>
  );
}

/** A value from a survey not yet submitted: a muted "draft" before it, said in its tooltip. */
export function DraftMark() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label="From a survey not yet submitted"
          data-draft=""
          className="mr-1.5 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1 py-px align-middle text-[0.625rem] leading-none font-semibold tracking-wide text-amber-800 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          draft
        </span>
      </TooltipTrigger>
      <TooltipContent>From a survey not yet submitted — the answer can still change.</TooltipContent>
    </Tooltip>
  );
}

/** A chosen question's column, or none for a code the survey's catalogue does not offer. */
export function answerColumn(answers: Nullable<IAnswerColumns>, code: string): IDataColumn<IWorkItemRow> | undefined {
  const field = answers?.fields.get(code);
  if (!field) return undefined;
  return {
    id: code,
    header: (
      <span title={field.label} className="block max-w-[16rem] truncate">
        {field.label}
      </span>
    ),
    label: field.label,
    numeric: field.kind === 'number' || field.kind === 'money' || field.kind === 'percent',
    className: 'text-xs',
    cell: (row) => (
      <AnswerValue field={field} value={row.answers?.[code]} draft={row.answersStatus === 'draft'} />
    )
  };
}
